import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../core/data/redux/store";
import {
    createEsicReason,
    updateEsicReason,
    fetchEsicReasonList,
    deleteEsicReason,
} from "../../../../core/data/redux/payroll/ESIC/Master/Reason/ReasonAction";
import { Drawer, message } from "antd";
import DeleteModal1 from "../../../../core/modals/deleteModal1";

export enum ReasonType {
    INCLUSION = "Exclusion",
    EXCLUSION = "Termination",
    TERMINATION = "Non-Deduction",
}

const EsicReasonModal: React.FC<any> = ({
    handleCancel,
    isModalVisible,
    modalType,
    selectedRowData,
    reasonData,
    setReasonData,
    setTableKey,
    setIsDeleteModalVisible,
    isDeleteModalVisible,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { loading } = useSelector((state: any) => state.esic.reason);

    const [errors, setErrors] = useState<any>({});

    useEffect(() => {
        if (modalType === "edit" && selectedRowData) {
            setReasonData({
                reason_type: selectedRowData.reason_type || "",
                reason_text: selectedRowData.reason_text || "",
            });
        } else {
            resetForm();
        }
    }, [modalType, selectedRowData]);

    const resetForm = () => {
        setReasonData({
            reason_type: "",
            reason_text: "",
        });
        setErrors({});
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
        field: string
    ) => {
        setReasonData({ ...reasonData, [field]: e.target.value });
        setErrors((prev: any) => ({ ...prev, [field]: "" }));
    };

    const validateForm = () => {
        let newErrors: any = {};
        if (!reasonData.reason_type)
            newErrors.reason_type = "Reason Type is required.";
        if (!reasonData.reason_text)
            newErrors.reason_text = "Reason Text is required.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            let result;
            if (modalType === "create") {
                result = await dispatch(createEsicReason(reasonData)).unwrap();
            } else if (modalType === "edit" && selectedRowData) {
                result = await dispatch(
                    updateEsicReason({ id: selectedRowData.reason_id, payload: reasonData })
                ).unwrap();
            }
            if (result) {
                message.success('Saved successfully');
                await dispatch(fetchEsicReasonList());
                setTableKey((prev: any) => prev + 1);
                resetForm();
                handleCancel();
            }
        } catch (error: any) {
            message.error(error.error || "An error occurred");
        }
    };

    const handleDelete = async (id: number) => {
        try {
            const result = await dispatch(deleteEsicReason(id));
            if (result.meta.requestStatus === "fulfilled") {
                message.success("Deleted successfully");
                await dispatch(fetchEsicReasonList());
                setTableKey((prev: any) => prev + 1);
                setIsDeleteModalVisible(false);
            } else {
                message.warning("Delete failed");
            }
        } catch {
            message.error("Something went wrong");
        }
    };

    return (
        <>
            <Drawer
                title={modalType === "edit" ? "Edit ESIC Reason" : "Add ESIC Reason"}
                placement="right"
                width={500}
                onClose={() => {
                    handleCancel();
                    resetForm();
                }}
                visible={isModalVisible}
                style={{ marginTop: 50, paddingBottom: 50 }}
            >
                <form onSubmit={handleSubmit}>
                    {/* Reason Type */}
                    <div className="mb-3">
                        <label className="form-label">
                            Reason Type <span className="text-danger">*</span>
                        </label>
                        <select
                            className="form-select"
                            value={reasonData.reason_type}
                            onChange={(e) => handleInputChange(e, "reason_type")}
                        >
                            <option value="">-- Select Reason Type --</option>
                            {Object.values(ReasonType).map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                        {errors.reason_type && (
                            <div className="text-danger">{errors.reason_type}</div>
                        )}
                    </div>

                    {/* Reason Text */}
                    <div className="mb-3">
                        <label className="form-label">
                            Reason Text <span className="text-danger">*</span>
                        </label>
                        <textarea
                            className="form-control"
                            rows={3}
                            value={reasonData.reason_text}
                            onChange={(e) => handleInputChange(e, "reason_text")}
                        />
                        {errors.reason_text && (
                            <div className="text-danger">{errors.reason_text}</div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-white border me-2"
                            onClick={() => {
                                resetForm();
                                handleCancel();
                            }}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading && (
                                <span className="spinner-border spinner-border-sm me-2" />
                            )}
                            {modalType === "edit" ? "Update Reason" : "Add Reason"}
                        </button>
                    </div>
                </form>
            </Drawer>

            <DeleteModal1
                isVisible={isDeleteModalVisible}
                onClose={() => setIsDeleteModalVisible(false)}
                onConfirm={() =>
                    selectedRowData?.reason_id && handleDelete(selectedRowData.reason_id)
                }
            />
        </>
    );
};

export default EsicReasonModal;
